import React from 'react';
import { FileUpload, SearchTypeObject } from './types';
import AssignmentDropdown from 'components/ui/AssignmentDropdown';
import { Checkbox, Dropdown, Grid, GridColumn, GridRow, Input } from 'semantic-ui-react';
import { AddNote, DeleteNote } from 'components/HelperDocuments/icons';
import produce from 'immer';
import EmailFields from './EmailFields';
import { getCurrentDateTimeForDateTimePicker } from 'lib/PublicFunctions';
import RequestNotes from './RequestNotes';
import {
  contactOptionsMatch,
  getVendorContactOptions,
  getVendorEmail,
  hasVendorEmail,
} from './continEmailDefaults';

interface PropTypes {
  searchTypeObjects: SearchTypeObject[];
  setSearchTypeObjects: React.Dispatch<React.SetStateAction<SearchTypeObject[]>>;
  assignees: any[];
  bulkAssignTo: any;
  bulkAbstractAssignTo: any;
  bulkAdditionalSendEmails: number[];
  bulkRequestNotes: string;
  bulkDueBy: string;
  bulkIsPriority: boolean;
  bulkIsSixMonthContin: boolean;
  scheduleSendMin: string;
  setScheduleSendMin: (min: string) => void;
  bulkSendEmail: boolean;
  setBulkSendEmail: (bulkSendEmail: boolean) => void;
  vendorEmailsById: Record<number, string>;
  fetchVendorEmail: (vendorId: number) => void;
  getEffectiveSearchTypeState: (searchTypeObject: SearchTypeObject) => {
    emailBody: string;
    vendorAttachments: FileUpload[];
  };
}

export const SearchInfoBySearchType = (props: PropTypes) => {
  const toAddressesDefaultKeysRef = React.useRef<Record<number, string>>({});
  const childSectionMaxWidth = 940;
  const childSectionWrapperStyle = {
    width: '100%',
    paddingLeft: 94,
    boxSizing: 'border-box' as const,
  };

  const getDefaultToAddressesVendorId = (searchTypeObject: SearchTypeObject) => {
    if (searchTypeObject.assigneeId > 0) {
      return searchTypeObject.assigneeIsEmployee ? null : searchTypeObject.assigneeId;
    }

    if (searchTypeObject.isAbstract && props.bulkAbstractAssignTo?.vendorId > 0) {
      return props.bulkAbstractAssignTo.vendorId;
    }

    if (!searchTypeObject.isAbstract && props.bulkAssignTo?.vendorId > 0) {
      return props.bulkAssignTo.vendorId;
    }

    return null;
  };

  const getDefaultToAddressesKey = (searchTypeObject: SearchTypeObject) => {
    const vendorId = getDefaultToAddressesVendorId(searchTypeObject);
    return `${vendorId ?? ''}:${getVendorEmail(props.vendorEmailsById, vendorId)}`;
  };

  const defaultToAddressesVendorIdsKey = React.useMemo(
    () => props.searchTypeObjects.map(s => getDefaultToAddressesVendorId(s) ?? '').join('|'),
    [props.searchTypeObjects, props.bulkAbstractAssignTo, props.bulkAssignTo]
  );
  const defaultToAddressesKey = React.useMemo(
    () => props.searchTypeObjects.map(s => `${s.searchTypeId}:${getDefaultToAddressesKey(s)}`).join('|'),
    [props.searchTypeObjects, props.bulkAbstractAssignTo, props.bulkAssignTo, props.vendorEmailsById]
  );

  React.useEffect(() => {
    const vendorIds = new Set(
      props.searchTypeObjects
        .map(getDefaultToAddressesVendorId)
        .filter((vendorId): vendorId is number => !!vendorId)
    );

    vendorIds.forEach(vendorId => {
      if (!hasVendorEmail(props.vendorEmailsById, vendorId)) {
        props.fetchVendorEmail(vendorId);
      }
    });
  }, [defaultToAddressesVendorIdsKey, props.vendorEmailsById, props.fetchVendorEmail]);

  React.useEffect(() => {
    props.setSearchTypeObjects(current =>
      produce(current, draft => {
        const currentSearchTypeIds = new Set<number>();

        draft.forEach(s => {
          currentSearchTypeIds.add(s.searchTypeId);
          const toAddressesKey = getDefaultToAddressesKey(s);

          if (toAddressesDefaultKeysRef.current[s.searchTypeId] === toAddressesKey) {
            return;
          }

          const toAddresses = getVendorContactOptions(
            props.vendorEmailsById,
            getDefaultToAddressesVendorId(s)
          );
          if (!contactOptionsMatch(s.emailInfo.toAddresses, toAddresses)) {
            s.emailInfo.toAddresses = toAddresses;
          }
          toAddressesDefaultKeysRef.current[s.searchTypeId] = toAddressesKey;
        });

        Object.keys(toAddressesDefaultKeysRef.current).forEach(searchTypeId => {
          if (!currentSearchTypeIds.has(Number(searchTypeId))) {
            delete toAddressesDefaultKeysRef.current[Number(searchTypeId)];
          }
        });
      })
    );
  }, [defaultToAddressesKey]);

  const handleIndividualSendEmailCheck = (checked: boolean, searchTypeId: number) => {
    setValueOfObject(checked, 'sendEmail', searchTypeId);
    const shouldCheckBulk =
      checked && props.searchTypeObjects.every(s => s.searchTypeId === searchTypeId || s.sendEmail);
    props.setBulkSendEmail(shouldCheckBulk);
  };

  const updateSearchTypeObject = (
    searchTypeId: number,
    update: (searchTypeObject: SearchTypeObject) => void
  ) => {
    props.setSearchTypeObjects(current =>
      produce(current, draft => {
        const currentObject = draft.find(s => s.searchTypeId === searchTypeId);
        if (!currentObject) {
          return;
        }

        update(currentObject);
      })
    );
  };

  const setAssignTo = (e: any, searchTypeId: number) => {
    updateSearchTypeObject(searchTypeId, searchTypeObject => {
      searchTypeObject.assigneeId = e.id;
      searchTypeObject.assigneeIsEmployee = !(e.vendorId > 0);
      searchTypeObject.showEmailFields = e.vendorId > 0;
      searchTypeObject.sendEmail = e.vendorId > 0;
    });
  };

  const setValueOfObject = (value: any, fieldName: string, searchTypeId: number) => {
    updateSearchTypeObject(searchTypeId, searchTypeObject => {
      (searchTypeObject as any)[fieldName] = value;
    });
  };

  const setEmailDataValueOfObject = (value: any, fieldName: string, searchTypeId: number) => {
    updateSearchTypeObject(searchTypeId, searchTypeObject => {
      (searchTypeObject.emailInfo as any)[fieldName] = value;
    });
  };

  const handleScheduleSendCheck = (checked: boolean, id: number) => {
    setEmailDataValueOfObject(checked, 'scheduleSend', id);
    if (props.scheduleSendMin === '') {
      const minDateTime = getCurrentDateTimeForDateTimePicker();
      setEmailDataValueOfObject(minDateTime, 'scheduleSendDateTime', id);
      props.setScheduleSendMin(minDateTime);
    }
  };

  return (
    <div>
      {props.searchTypeObjects.map(s => {
        const effectiveState = props.getEffectiveSearchTypeState(s);

        return (
          <React.Fragment key={s.searchTypeId}>
            <Grid style={{ marginTop: 10 }}>
              <GridRow>
                <GridColumn style={{ width: 78 }}>
                  <div style={{ display: 'flex', flexDirection: 'row', paddingTop: 8 }}>
                    <b>{`${s.searchCode}: `}</b>
                    {s.openNote || s.showEmailFields ? (
                      <DeleteNote
                        style={{ marginLeft: 'auto', marginTop: 3, cursor: 'pointer' }}
                        onClick={() =>
                          setValueOfObject(
                            false,
                            s.showEmailFields ? 'showEmailFields' : 'openNote',
                            s.searchTypeId
                          )
                        }
                      />
                    ) : (
                      <AddNote
                        style={{ marginLeft: 'auto', marginTop: 3, cursor: 'pointer' }}
                        onClick={() =>
                          setValueOfObject(
                            true,
                            (s.assigneeId && !s.assigneeIsEmployee) ||
                              (s.isAbstract && !s.assigneeId && props.bulkAbstractAssignTo?.vendorId > 0) ||
                              (!s.isAbstract && !s.assigneeId && props.bulkAssignTo?.vendorId > 0)
                              ? 'showEmailFields'
                              : 'openNote',
                            s.searchTypeId
                          )
                        }
                      />
                    )}
                  </div>
                </GridColumn>
                <GridColumn style={{ width: 320 }}>
                  <AssignmentDropdown
                    assignees={props.assignees}
                    buttonText={`Select ${s.isAbstract ? 'abstract' : ''} employee/vendor`}
                    onClick={(e: any) => {
                      setAssignTo(e, s.searchTypeId);
                    }}
                    prevSelected={
                      s.assigneeId
                        ? {
                            ...props.assignees[s.assigneeIsEmployee ? 0 : 1].items.find(
                              (a: any) => a.id === s.assigneeId
                            ),
                            categoryIndex: s.assigneeIsEmployee ? 0 : 1,
                          }
                        : s.isAbstract
                        ? props.bulkAbstractAssignTo
                        : props.bulkAssignTo
                    }
                  />
                </GridColumn>
                <GridColumn style={{ width: 320 }}>
                  <Dropdown
                    multiple
                    selection
                    search
                    placeholder="Additional users"
                    options={
                      (props.assignees || [])[0]?.items.map((i: any) => ({
                        value: i.id,
                        text: i.name,
                        key: i.id,
                      })) || []
                    }
                    onChange={(e, { value }) =>
                      setValueOfObject(value as number[], 'additionalSendEmails', s.searchTypeId)
                    }
                    closeOnChange
                    fluid
                    style={{ minHeight: 48, width: 300 }}
                    value={s.additionalSendEmails ?? props.bulkAdditionalSendEmails}
                  />
                </GridColumn>
                <GridColumn style={{ width: 190 }}>
                  <Input
                    style={{ width: 185, height: 48 }}
                    value={s.dueBy ?? props.bulkDueBy}
                    type="date"
                    onChange={e => setValueOfObject(e.target.value, 'dueBy', s.searchTypeId)}
                    fluid
                    label={{ basic: true, content: 'Due', style: { alignContent: 'center' } }}
                  />
                </GridColumn>
                <GridColumn style={{ width: 455, paddingTop: 10 }}>
                  <span style={{ marginTop: 5, marginLeft: 3 }}>
                    <Checkbox
                      label="Is Priority"
                      checked={s.isPriority ?? props.bulkIsPriority}
                      onChange={(e, { checked }) => setValueOfObject(checked, 'isPriority', s.searchTypeId)}
                    />
                    <Checkbox
                      style={{ marginLeft: 8 }}
                      label="Charge Full"
                      checked={s.isSixMonthContin ?? props.bulkIsSixMonthContin}
                      onChange={(e, { checked }) =>
                        setValueOfObject(checked, 'isSixMonthContin', s.searchTypeId)
                      }
                    />
                  </span>
                </GridColumn>
              </GridRow>
              {s.showEmailFields ? (
                <GridRow style={{ minHeight: 350 }}>
                  <div style={childSectionWrapperStyle}>
                    <div style={{ maxWidth: childSectionMaxWidth }}>
                      <EmailFields
                        disabled={!s.sendEmail}
                        selectedContacts={s.emailInfo.toAddresses}
                        selectedCcEmails={s.emailInfo.ccAddresses}
                        selectedBccEmails={s.emailInfo.bccAddresses}
                        subject={s.emailInfo.subject}
                        emailBodyPreview={s.sendEmail ? effectiveState.emailBody : ''}
                        scheduleSend={s.emailInfo.scheduleSend}
                        scheduleSendDateTime={s.emailInfo.scheduleSendDateTime}
                        scheduleSendMin={props.scheduleSendMin}
                        contactsList={getVendorContactOptions(
                          props.vendorEmailsById,
                          getDefaultToAddressesVendorId(s)
                        )}
                        setSelectedContacts={selections =>
                          setEmailDataValueOfObject(selections, 'toAddresses', s.searchTypeId)
                        }
                        setSelectedCcEmails={selections =>
                          setEmailDataValueOfObject(selections, 'ccAddresses', s.searchTypeId)
                        }
                        setSelectedBccEmails={selections =>
                          setEmailDataValueOfObject(selections, 'bccAddresses', s.searchTypeId)
                        }
                        setSubject={subject => setEmailDataValueOfObject(subject, 'subject', s.searchTypeId)}
                        handleScheduleSendCheck={checked => handleScheduleSendCheck(checked, s.searchTypeId)}
                        setScheduleSendDateTime={d =>
                          setEmailDataValueOfObject(d, 'scheduleSendDateTime', s.searchTypeId)
                        }
                        sendEmailChecked={s.sendEmail ?? props.bulkSendEmail}
                        handleSendEmailCheck={checked =>
                          handleIndividualSendEmailCheck(checked, s.searchTypeId)
                        }
                        attachments={effectiveState.vendorAttachments}
                        setAttachments={attachments =>
                          setValueOfObject(attachments, 'vendorEmailAttachments', s.searchTypeId)
                        }
                        requestNotes={
                          <RequestNotes
                            width={'100%'}
                            marginLeft={0}
                            marginTop={16}
                            value={s.requestNotes ?? props.bulkRequestNotes ?? ''}
                            onChange={value => setValueOfObject(value, 'requestNotes', s.searchTypeId)}
                          />
                        }
                      />
                    </div>
                  </div>
                </GridRow>
              ) : (
                s.openNote && (
                  <div style={childSectionWrapperStyle}>
                    <div style={{ maxWidth: 616 }}>
                      <RequestNotes
                        width={'100%'}
                        marginLeft={0}
                        marginTop={8}
                        value={s.requestNotes ?? props.bulkRequestNotes ?? ''}
                        onChange={value => setValueOfObject(value, 'requestNotes', s.searchTypeId)}
                      />
                    </div>
                  </div>
                )
              )}
            </Grid>
          </React.Fragment>
        );
      })}
    </div>
  );
};
